import React, { useState, useEffect } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Delete,
  Search,
  Phone,
  Check,
  Close,
  Message
} from '@mui/icons-material';
import twilioService, {
  TwilioPhoneNumber,
  AvailablePhoneNumber
} from '../../services/twilioService';
import { useOrganization } from '../../pages/Organization/OrganizationContext';

const TwilioPhoneNumbersComponent: React.FC = () => {
  const { organization } = useOrganization();
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phoneNumbers, setPhoneNumbers] = useState<TwilioPhoneNumber[]>([]);
  const [areaCode, setAreaCode] = useState('');
  const [friendlyName, setFriendlyName] = useState('');
  const [country, setCountry] = useState('US');
  const [availableNumbers, setAvailableNumbers] = useState<AvailablePhoneNumber[]>([]);
  const [openSearchDialog, setOpenSearchDialog] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState<AvailablePhoneNumber | null>(null);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 5,
    page: 0,
  });
  const [searchPaginationModel, setSearchPaginationModel] = useState({
    pageSize: 5,
    page: 0,
  });

  useEffect(() => {
    if (organization?._id) {
      fetchPhoneNumbers();
    }
  }, [organization]);

  const fetchPhoneNumbers = async () => {
    if (!organization?._id) return;

    setLoading(true);
    setError(null);

    try {
      const data = await twilioService.listPhoneNumbers();
      setPhoneNumbers(data);
    } catch (err) {
      setError('Failed to fetch phone numbers');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const searchPhoneNumbers = async () => {
    if (!organization?._id || !areaCode) return;

    setSearchLoading(true);
    setError(null);

    try {
      const data = await twilioService.searchPhoneNumbers(areaCode, country);
      setAvailableNumbers(data);
      setOpenSearchDialog(true);
    } catch (err) {
      setError('Failed to search for available phone numbers');
      console.error(err);
    } finally {
      setSearchLoading(false);
    }
  };

  const purchasePhoneNumber = async () => {
    if (!organization?._id || !selectedNumber || !friendlyName) return;

    setPurchasing(true);
    setError(null);

    try {
      await twilioService.purchasePhoneNumber(
        selectedNumber.phoneNumber,
        friendlyName
      );

      // Close dialog and reset form
      setOpenSearchDialog(false);
      setAreaCode('');
      setFriendlyName('');
      setSelectedNumber(null);
      setAvailableNumbers([]);

      // Refresh phone numbers list
      fetchPhoneNumbers();
    } catch (err) {
      setError('Failed to purchase phone number');
      console.error(err);
    } finally {
      setPurchasing(false);
    }
  };

  const releasePhoneNumber = async (numberSid: string) => {
    if (!organization?._id) return;

    setReleasing(true);
    setError(null);

    try {
      await twilioService.releasePhoneNumber(numberSid);
      // Refresh the list after releasing
      fetchPhoneNumbers();
    } catch (err) {
      setError('Failed to release phone number');
      console.error(err);
    } finally {
      setReleasing(false);
    }
  };

  const handleCloseSearchDialog = () => {
    setOpenSearchDialog(false);
    setSelectedNumber(null);
  };

  const selectNumber = (number: AvailablePhoneNumber) => {
    setSelectedNumber(number);
    setFriendlyName(number.friendlyName || number.phoneNumber);
  };

  const formatPhoneNumber = (phoneNumber: string) => {
    return phoneNumber.replace(/^\+1/, '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  };

  const columns: GridColDef[] = [
    {
      field: 'phoneNumber',
      headerName: 'Phone Number',
      flex: 1,
      valueFormatter: (params) => formatPhoneNumber(params.value)
    },
    { field: 'friendlyName', headerName: 'Friendly Name', flex: 1 },
    {
      field: 'capabilities',
      headerName: 'Capabilities',
      flex: 1,
      renderCell: (params) => (
        <div className="flex gap-1">
          {params.value.sms && (
            <div title="SMS">
              <span className="px-2 py-1 text-xs font-medium border border-blue-500 text-blue-600 rounded-full">
                SMS
              </span>
            </div>
          )}
          {params.value.mms && (
            <div title="MMS">
              <span className="px-2 py-1 text-xs font-medium border border-purple-500 text-purple-600 rounded-full">
                MMS
              </span>
            </div>
          )}
          {params.value.voice && (
            <div title="Voice">
              <span className="px-2 py-1 text-xs font-medium border border-green-500 text-green-600 rounded-full">
                Voice
              </span>
            </div>
          )}
        </div>
      )
    },
    {
      field: 'dateCreated',
      headerName: 'Created',
      flex: 1,
      valueFormatter: (params) => new Date(params.value).toLocaleDateString()
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: (params) => (
        <div title="Release">
          <button
            onClick={() => releasePhoneNumber(params.row.sid)}
            className={`p-1 rounded-full ${releasing ? 'text-red-300 cursor-not-allowed' : 'text-red-600 hover:text-red-800 hover:bg-red-100'
              }`}
            disabled={releasing}
          >
            <Delete fontSize="small" />
          </button>
        </div>
      ),
    },
  ];

  const searchResultColumns: GridColDef[] = [
    {
      field: 'phoneNumber',
      headerName: 'Phone Number',
      flex: 1,
      valueFormatter: (params) => formatPhoneNumber(params.value)
    },
    { field: 'friendlyName', headerName: 'Friendly Name', flex: 1 },
    { field: 'locality', headerName: 'City', flex: 1 },
    { field: 'region', headerName: 'State', width: 80 },
    {
      field: 'capabilities',
      headerName: 'Capabilities',
      flex: 1,
      renderCell: (params) => (
        <div className="flex gap-1">
          {params.value.sms && (
            <div title="SMS">
              <span className="px-2 py-1 text-xs font-medium border border-blue-500 text-blue-600 rounded-full">
                SMS
              </span>
            </div>
          )}
          {params.value.mms && (
            <div title="MMS">
              <span className="px-2 py-1 text-xs font-medium border border-purple-500 text-purple-600 rounded-full">
                MMS
              </span>
            </div>
          )}
          {params.value.voice && (
            <div title="Voice">
              <span className="px-2 py-1 text-xs font-medium border border-green-500 text-green-600 rounded-full">
                Voice
              </span>
            </div>
          )}
        </div>
      )
    },
    {
      field: 'actions',
      headerName: 'Select',
      width: 100,
      renderCell: (params) => (
        <div title="Select Number">
          <button
            onClick={() => selectNumber(params.row)}
            className="p-1 text-blue-600 hover:text-blue-800 rounded-full hover:bg-blue-100"
          >
            <Check fontSize="small" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-lg shadow-md">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold">Twilio Phone Numbers</h2>
      </div>
      <hr className="border-gray-200" />
      <div className="p-6">
        {error && (
          <div className="mb-4 px-4 py-3 bg-red-100 text-red-700 rounded-md" role="alert">
            {error}
          </div>
        )}

        <div className="mb-6">
          <p className="mb-4">
            Search for available phone numbers by area code:
          </p>

          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Area Code
              </label>
              <input
                type="text"
                value={areaCode}
                onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, '').substring(0, 3))}
                required
                placeholder="e.g. 415"
                maxLength={3}
                className="w-full md:w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country
              </label>
              <input
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                maxLength={2}
                className="w-full md:w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <button
              onClick={searchPhoneNumbers}
              disabled={searchLoading || areaCode.length !== 3}
              className={`flex items-center px-4 py-2 rounded-md text-white font-medium w-full md:w-auto ${searchLoading || areaCode.length !== 3
                ? 'bg-blue-300 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
                }`}
            >
              {searchLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  <span>Searching...</span>
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </>
              )}
            </button>
          </div>
        </div>

        <hr className="my-4 border-gray-200" />

        <h3 className="text-lg font-semibold mb-2">Your Phone Numbers</h3>

        {loading ? (
          <div className="flex justify-center items-center p-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : phoneNumbers.length === 0 ? (
          <div className="p-6 text-center bg-gray-50 rounded-lg">
            <Phone className="text-gray-300 mx-auto" style={{ fontSize: 48 }} />
            <h3 className="mt-2 text-lg font-medium">No Phone Numbers</h3>
            <p className="text-sm text-gray-500 mb-4">
              You haven't purchased any phone numbers yet. Use the search above to find and purchase a phone number.
            </p>
          </div>
        ) : (
          <div className="h-[400px] w-full">
            <DataGrid
              rows={phoneNumbers.map(num => ({ ...num, id: num.sid }))}
              columns={columns}
              pagination
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 20]}
              className="border-0 shadow-none"
            />
          </div>
        )}
      </div>

      {/* Dialog to show search results and purchase a number */}
      {openSearchDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-semibold">Search Results</h3>
            </div>
            <div className="p-6">
              {availableNumbers.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Message className="text-gray-300" style={{ fontSize: 48 }} />
                  <h3 className="mt-2 text-lg font-medium">No Numbers Found</h3>
                  <p className="text-sm text-gray-500">
                    No phone numbers available with the specified area code. Try a different area code.
                  </p>
                </div>
              ) : (
                <>
                  <p className="mb-4 text-sm">
                    Select a phone number to purchase:
                  </p>

                  <div className="h-[300px] w-full mb-5">
                    <DataGrid
                      rows={availableNumbers.map((num, idx) => ({ ...num, id: idx }))}
                      columns={searchResultColumns}
                      pagination
                      paginationModel={searchPaginationModel}
                      onPaginationModelChange={setSearchPaginationModel}
                      pageSizeOptions={[5, 10, 20]}
                      className="border-0 shadow-none"
                    />
                  </div>

                  {selectedNumber && (
                    <div className="mt-4">
                      <hr className="mb-4 border-gray-200" />
                      <h4 className="text-lg font-semibold mb-3">Purchase Selected Number</h4>
                      <p className="mb-4">
                        Selected Number: <strong>{formatPhoneNumber(selectedNumber.phoneNumber)}</strong>
                      </p>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Friendly Name
                        </label>
                        <input
                          type="text"
                          value={friendlyName}
                          onChange={(e) => setFriendlyName(e.target.value)}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="px-6 py-3 bg-gray-50 flex justify-end gap-2 border-t border-gray-200">
              <button
                onClick={handleCloseSearchDialog}
                className="flex items-center px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-md"
              >
                <Close className="w-4 h-4 mr-2" />
                Cancel
              </button>
              {selectedNumber && (
                <button
                  onClick={purchasePhoneNumber}
                  disabled={purchasing || !friendlyName}
                  className={`flex items-center px-4 py-2 rounded-md text-white font-medium ${purchasing || !friendlyName
                    ? 'bg-blue-300 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                >
                  {purchasing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      <span>Purchasing...</span>
                    </>
                  ) : (
                    <>
                      <Phone className="w-4 h-4 mr-2" />
                      Purchase
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TwilioPhoneNumbersComponent; 